import type { OperationalDismissedAlert, StoredDismissedAlert } from './types';
import { DateTime } from 'luxon';

const CURRENT_VERSION = 2;

/**
 * Serializes an alert object into a string format.
 */
export const serializeAlert = (alert: StoredDismissedAlert): string => {
  return [
    CURRENT_VERSION,
    alert.id,
    alert.origin,
    alert.dismissedAt.setZone('utc').toUnixInteger(),
  ].join('.');
};

/**
 * Unserializes a string into an alert object.
 */
export const unserializeAlert = (
  serializedAlert: string,
): StoredDismissedAlert | undefined => {
  let [
    versionArg = undefined,
    id = undefined,
    location = undefined,
    timestampArg = undefined,
  ] = serializedAlert.split('.');

  if (typeof id === 'undefined') {
    return undefined;
  }

  let version = versionArg ? parseInt(versionArg, 10) : 1;
  let timestamp = timestampArg ? parseInt(timestampArg, 10) : 0;

  if (version === 1) {
    timestamp = Math.round(timestamp / 1000);
  }

  let dismissedAt = DateTime.fromSeconds(timestamp, {
    zone: 'utc',
  }).toLocal();

  return {
    id,
    dismissedAt: dismissedAt.isValid ? dismissedAt : DateTime.now(),
    origin: location ?? '',
  };
};

/**
 * Generates a comparison key for an alert based on its ID and origin.
 */
export const getComparisonKey = (alert: OperationalDismissedAlert): string => {
  return `${alert.id}.${alert.origin}`;
};

/**
 * Filters out duplicate alerts based on their comparison key.
 * Always keeps the latest alert for each unique comparison key.
 */
export const uniqueAlerts = <
  Alert extends OperationalDismissedAlert = OperationalDismissedAlert,
>(
  nonUnique: Alert[],
): Alert[] => {
  let inserted: string[] = [];

  return (
    nonUnique
      // Reverse the array to keep the latest alert for each key
      .reverse()
      // Filter out duplicates
      .filter(alert => {
        let key = getComparisonKey(alert);

        if (inserted.includes(key)) {
          return false;
        }

        inserted.push(key);

        return true;
      })
      // Reverse the array back to the original order
      .reverse()
  );
};
